/**
 * SyproFlow Feedback Widget v2.0.0
 * =================================
 * 3-level accuracy rating for pilot phase.
 * Ratings: accurate | minor_fixes | major_errors
 * Reads user email from localStorage (no prop needed).
 * Sends feedback to /api/v1/feedback endpoint.
 *
 * Usage: <FeedbackWidget documentName="invoice.pdf" documentType="invoice" modelUsed="haiku" pageCount={5} />
 */

import React, { useState, useCallback } from 'react';

const getApiBase = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return import.meta?.env?.VITE_API_BASE_URL || 'http://localhost:8000';
};

const API = getApiBase();

const RATINGS = [
  { value: 'accurate',     label: 'Accurate',     color: '#16a34a', bg: '#f0fdf4', border: '#86efac', emoji: '✓' },
  { value: 'minor_fixes',  label: 'Minor fixes',  color: '#b45309', bg: '#fffbeb', border: '#fcd34d', emoji: '~' },
  { value: 'major_errors', label: 'Major errors', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', emoji: '✗' },
];

export default function FeedbackWidget({ documentName, documentType, modelUsed, pageCount }) {
  const [rating, setRating]       = useState(null);
  const [comment, setComment]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]     = useState(false);

  const submitFeedback = useCallback(async (selectedRating, userComment = '') => {
    setSending(true);
    const userEmail = localStorage.getItem('syproflow_email') || null;
    try {
      await fetch(`${API}/api/v1/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating:        selectedRating,
          comment:       userComment || null,
          document_name: documentName || null,
          document_type: documentType || null,
          model_used:    modelUsed || null,
          page_count:    pageCount || null,
          user_email:    userEmail,
        })
      });
    } catch (err) {
      console.error('Feedback error:', err);
    } finally {
      setSending(false);
      setSubmitted(true);
    }
  }, [documentName, documentType, modelUsed, pageCount]);

  const handleRating = (value) => {
    setRating(value);
  };

  const handleSubmit = () => {
    if (!rating) return;
    submitFeedback(rating, comment);
  };

  if (submitted) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '8px 16px', background: '#f0fdf4', borderRadius: '8px',
        border: '1px solid #bbf7d0', fontSize: '13px', color: '#15803d'
      }}>
        ✓ Thanks for your feedback!
      </div>
    );
  }

  const selectedRating = RATINGS.find(r => r.value === rating);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '10px',
      padding: '12px 16px', background: '#fafafa', borderRadius: '10px',
      border: '1px solid #e5e7eb'
    }}>
      {/* Label */}
      <span style={{ fontSize: '13px', color: '#6b7280' }}>How accurate was this extraction?</span>

      {/* 3-button rating row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {RATINGS.map(r => (
          <button
            key={r.value}
            onClick={() => handleRating(r.value)}
            disabled={sending}
            style={{
              background:   rating === r.value ? r.bg  : '#fff',
              border:       rating === r.value ? `2px solid ${r.border}` : '1px solid #d1d5db',
              color:        rating === r.value ? r.color : '#374151',
              borderRadius: '8px',
              padding:      '5px 14px',
              cursor:       'pointer',
              fontSize:     '13px',
              fontWeight:   rating === r.value ? 600 : 400,
              transition:   'all 0.15s',
              opacity:      sending ? 0.5 : 1,
            }}
          >
            {r.emoji} {r.label}
          </button>
        ))}
      </div>

      {/* Comment box — always shown once a rating is selected */}
      {rating && !submitted && (
        <div style={{ display: 'flex', gap: '8px', animation: 'feedbackSlide 0.2s ease-out' }}>
          <input
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={
              rating === 'accurate'
                ? 'Any comments? (optional)'
                : 'What needs fixing? (optional)'
            }
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            style={{
              flex: 1, border: '1px solid #d1d5db', borderRadius: '8px',
              padding: '8px 12px', fontSize: '13px', outline: 'none',
              transition: 'border-color 0.15s'
            }}
            onFocus={e => e.target.style.borderColor = '#ea580c'}
            onBlur={e  => e.target.style.borderColor = '#d1d5db'}
            autoFocus
          />
          <button
            onClick={handleSubmit}
            disabled={sending}
            style={{
              background: selectedRating ? selectedRating.color : '#ea580c',
              color: '#fff', border: 'none', borderRadius: '8px',
              padding: '8px 16px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 500,
              opacity: sending ? 0.5 : 1,
              transition: 'opacity 0.15s'
            }}
          >{sending ? '...' : 'Send'}</button>
        </div>
      )}

      <style>{`
        @keyframes feedbackSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
